import { Font, StyleSheet } from "@react-pdf/renderer";

/**
 * Shared look for every CSA document.
 *
 * One place so the PDF and the Word renderer cannot drift apart — the whole
 * point of composing a model first is that the renderers only draw (design
 * §7a). Anything that decides *what* appears belongs in the composer, not here.
 */

/* ------------------------------------------------------------------ */
/* fonts                                                               */
/* ------------------------------------------------------------------ */

/**
 * Montserrat, from `public/fonts` — the same faces the brand uses elsewhere,
 * served from our own origin rather than fetched from Google at render time.
 * A report must build with no network beyond S3.
 *
 * Runs at import, before StyleSheet.create below, because the styles have to
 * name a family that is already registered: react-pdf does **not** fall back
 * when a family is missing, it throws "Font family not registered" at render
 * time. So the family is decided here and the styles use whatever came back.
 *
 * Browser only. The `src` values are origin-relative URLs — a browser resolves
 * them against the app's host, but react-pdf's Node build treats them as
 * filesystem paths and tries to open `C:\fonts\...`. Returning Helvetica in
 * Node is what makes this document renderable under test, and what would let
 * it move server-side later without a rewrite.
 */
function registerFonts() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return "Helvetica";
  }

  try {
    Font.register({
      family: "Montserrat",
      fonts: [
        { src: "/fonts/Montserrat-Regular.ttf", fontWeight: 400 },
        { src: "/fonts/Montserrat-Light.ttf", fontWeight: 300 },
        { src: "/fonts/Montserrat-Bold.ttf", fontWeight: 700 },
        { src: "/fonts/Montserrat-Italic.ttf", fontWeight: 400, fontStyle: "italic" },
      ],
    });

    // Long observation text in a narrow table cell has to break somewhere.
    // react-pdf's default hyphenation splits mid-word in ways that read badly
    // in a client document, so words wrap whole instead.
    Font.registerHyphenationCallback((word) => [word]);

    return "Montserrat";
  } catch (e) {
    // A missing font file must not stop a report being produced.
    console.warn("CSA PDF: could not register Montserrat, using Helvetica —", e.message);
    return "Helvetica";
  }
}

export const FONT = registerFonts();

/* ------------------------------------------------------------------ */
/* palette                                                             */
/* ------------------------------------------------------------------ */

/** Lifted from the approved prototype, so print matches the screen. */
export const C = {
  ink: "#1E2A28",
  slate: "#5C6B68",
  rule: "#DCE3E1",
  sunk: "#F1F4F3",
  white: "#FFFFFF",
  teal: "#307268",
  tealDark: "#245952",
  gold: "#EFC71D",
  cream: "#FFF8DC",

  yes: "#2E7D4F",
  partial: "#B8860B",
  no: "#C0392B",
  na: "#7C8A87",
};

/** Status drives a colour in exactly one place, so the two layouts agree. */
export const statusColour = (s) =>
  s === "Yes" ? C.yes : s === "Partial" ? C.partial : s === "No" ? C.no : C.na;

/* ------------------------------------------------------------------ */
/* styles                                                             */
/* ------------------------------------------------------------------ */

export const styles = StyleSheet.create({
  page: {
    fontFamily: FONT,
    fontSize: 9,
    color: C.ink,
    paddingTop: 52,
    paddingBottom: 46,
    paddingHorizontal: 34,
    lineHeight: 1.45,
  },

  /* ---- running head and foot ---- */
  /*
     The wordmark in the running head. Sized off its own 3.89:1 ratio so it is
     not stretched, and kept small: it sits beside the client's name, not over
     it. The cover carries the logo already, which is why Chrome is not drawn
     on page one.
  */
  runningLogo: { width: 62, height: 16, objectFit: "contain", marginRight: 10 },
  runningHeadLeft: { flexDirection: "row", alignItems: "center" },

  runningHead: {
    position: "absolute",
    top: 20,
    left: 34,
    right: 34,
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 7,
    color: C.slate,
    borderBottomWidth: 0.5,
    borderBottomColor: C.rule,
    paddingBottom: 5,
  },
  runningFoot: {
    position: "absolute",
    bottom: 20,
    left: 34,
    right: 34,
    flexDirection: "row",
    // Three items now: reference, issue, page. space-between keeps the issue
    // centred without measuring anything.
    justifyContent: "space-between",
    fontSize: 7,
    color: C.slate,
    borderTopWidth: 0.5,
    borderTopColor: C.rule,
    paddingTop: 5,
  },

  /* ---- cover ---- */
  cover: { flexGrow: 1, justifyContent: "center", paddingHorizontal: 20 },
  coverRule: { height: 3, width: 74, backgroundColor: C.gold, marginBottom: 22 },
  coverKicker: {
    fontSize: 9,
    letterSpacing: 2,
    color: C.teal,
    fontWeight: 700,
    marginBottom: 12,
  },
  coverTitle: { fontSize: 27, fontWeight: 700, lineHeight: 1.2, marginBottom: 6 },
  coverSite: { fontSize: 14, color: C.slate, marginBottom: 30 },
  coverGrid: { flexDirection: "row", flexWrap: "wrap", marginTop: 8 },
  coverCell: { width: "50%", marginBottom: 14, paddingRight: 12 },
  coverLabel: {
    fontSize: 7,
    letterSpacing: 1.1,
    color: C.slate,
    fontWeight: 700,
    marginBottom: 3,
  },
  coverValue: { fontSize: 11 },

  /* ---- the branded cover, matching HSE and Electrical ----

     Measured off the HSE cover so the three products line up. The artwork is a
     frame with no wording on it; everything readable is drawn here.

     The two blocks exist because the text has to sit on a photograph. Dark ink
     straight onto the picture is unreadable, which is what the first version
     did — the details block gives it a gold ground, and the title is set in
     white where the image is dark. */
  coverPage: { position: 'relative', padding: 0 },
  coverBackdrop: { position: 'absolute', top: 0, left: 0, width: 595.28, height: 841.89 },
  coverImage: { width: 595.28, height: 841.89 },

  /* The gold panel the client details sit on. */
  coverPanel: {
    position: 'absolute',
    left: 30,
    top: 424,
    width: 352,
    height: 176,
    backgroundColor: C.gold,
  },
  coverPanelInner: { paddingTop: 26, paddingLeft: 28 },
  coverDetailRow: { flexDirection: 'row', marginBottom: 12 },
  coverDetailLabel: { width: 74, fontSize: 10.5, fontWeight: 700, color: C.tealDark },
  coverDetailValue: { fontSize: 10.5, color: C.tealDark, flex: 1 },

  /* The product name, in white, low and over the picture. */
  coverProduct: { position: 'absolute', left: 168, top: 676, width: 380 },
  coverProductText: { fontSize: 33, fontWeight: 700, lineHeight: 1.16, color: '#FFFFFF' },
  coverYearRow: { flexDirection: 'row', alignItems: 'center', marginTop: 10 },
  coverYearLine: { width: 92, height: 2.5, backgroundColor: '#FFFFFF', marginRight: 12 },
  coverYear: { fontSize: 17, fontWeight: 700, color: '#FFFFFF' },

  /* ---- the details page the cover used to carry ---- */
  detailsHead: { fontSize: 16, fontWeight: 700, marginBottom: 4 },
  detailsSite: { fontSize: 12, color: C.slate, marginBottom: 22 },

  /* ---- headings ---- */
  h1: {
    fontSize: 15,
    fontWeight: 700,
    color: C.tealDark,
    marginBottom: 3,
  },
  h1Rule: { height: 2, backgroundColor: C.gold, width: 40, marginBottom: 12 },
  h2: { fontSize: 11, fontWeight: 700, marginTop: 14, marginBottom: 5 },
  h3: { fontSize: 9.5, fontWeight: 700, color: C.teal, marginTop: 10, marginBottom: 4 },
  body: { fontSize: 9.5, marginBottom: 7, textAlign: "justify" },
  muted: { fontSize: 8.5, color: C.slate },

  /* ---- score tables ---- */
  scoreHero: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: C.sunk,
    borderLeftWidth: 3,
    borderLeftColor: C.gold,
    padding: 14,
    marginBottom: 14,
  },
  scoreBig: { fontSize: 34, fontWeight: 700, color: C.tealDark, marginRight: 16 },
  scoreBand: { fontSize: 13, fontWeight: 700 },

  tRow: { flexDirection: "row", borderBottomWidth: 0.5, borderBottomColor: C.rule },
  tHead: {
    flexDirection: "row",
    backgroundColor: C.tealDark,
    color: C.white,
    borderBottomWidth: 0,
  },
  tHeadCell: { fontSize: 7.5, fontWeight: 700, padding: 6, letterSpacing: 0.4 },
  tCell: { fontSize: 8.5, padding: 6 },
  tCellTight: { fontSize: 8, padding: 5 },
  zebra: { backgroundColor: C.sunk },

  /* ---- the findings register ---- */
  regHead: {
    flexDirection: "row",
    backgroundColor: C.tealDark,
    color: C.white,
  },
  regHeadCell: {
    fontSize: 6.8,
    fontWeight: 700,
    padding: 5,
    letterSpacing: 0.35,
    borderRightWidth: 0.5,
    borderRightColor: "rgba(255,255,255,0.25)",
  },
  regRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: C.rule,
    // Keeps a finding intact across a page break where it fits at all.
    minPresenceAhead: 40,
  },
  regCell: {
    fontSize: 7.4,
    padding: 5,
    borderRightWidth: 0.5,
    borderRightColor: C.rule,
  },
  statusPill: {
    fontSize: 6.8,
    fontWeight: 700,
    color: C.white,
    paddingVertical: 2,
    paddingHorizontal: 4,
    borderRadius: 2,
    textAlign: "center",
  },
  thumbRow: { flexDirection: "row", flexWrap: "wrap" },
  thumb: { width: 26, height: 26, marginRight: 2, marginBottom: 2, objectFit: "cover" },

  /* ---- scope ---- */
  scopeItem: {
    borderLeftWidth: 2,
    borderLeftColor: C.gold,
    backgroundColor: C.cream,
    padding: 8,
    marginBottom: 6,
  },
  scopeName: { fontSize: 9, fontWeight: 700, marginBottom: 2 },

  empty: { fontSize: 9, color: C.slate, fontStyle: "italic", marginTop: 6 },
});

/* ------------------------------------------------------------------ */
/* Elaborate layout                                                    */
/* ------------------------------------------------------------------ */

/**
 * One entry per finding, set out in full — roughly four to a page.
 *
 * Separated from `styles` so the register and the entry cannot accidentally
 * share a rule and drift together. Both draw the same model; only the shape
 * differs.
 */
/**
 * The preview watermark.
 *
 * A preview renders the same document as an issue, minus the issue line — so a
 * saved preview was a complete, professional-looking CSA report carrying no
 * number and leaving no trace in the issue trail. If one reached a client,
 * nothing on it would say which document it was, and our own record would show
 * it had never been sent.
 *
 * A flat pale colour rather than `opacity`: opacity support varies across
 * react-pdf versions and a watermark that silently fails to render is worse
 * than none, because it is trusted. This colour is light enough to read
 * through and dark enough to survive a photocopy.
 */
export const mark = StyleSheet.create({
  wrap: {
    position: "absolute",
    top: "42%",
    left: 0,
    right: 0,
    alignItems: "center",
    // Across the page, so it cannot be cropped off by trimming an edge.
    transform: "rotate(-30deg)",
  },
  text: {
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: 46,
    letterSpacing: 3,
    color: "#EFC9C4",
  },
  sub: {
    fontFamily: FONT,
    fontSize: 12,
    letterSpacing: 2,
    color: "#EFC9C4",
    marginTop: 4,
  },
});

export const elab = StyleSheet.create({
  pillar: {
    fontSize: 13,
    fontWeight: 700,
    color: C.tealDark,
    marginTop: 18,
    marginBottom: 2,
  },
  subPillar: {
    fontSize: 10,
    fontWeight: 700,
    color: C.teal,
    letterSpacing: 0.3,
    marginTop: 12,
    marginBottom: 6,
    paddingBottom: 3,
    borderBottomWidth: 1,
    borderBottomColor: C.rule,
  },


  // The framework's own sentence. Set slightly larger than the lines beneath
  // because it is the finding; the rest qualifies it.



  /* ---- each finding as a table ----

     The loose label-and-body lines read well for one finding and badly for
     forty: nothing lines up down the page, so an eye scanning for "Action"
     has to re-find it in every entry. A ruled two-column table puts every
     label in the same place. */
  tbl: {
    borderWidth: 0.6,
    borderColor: C.rule,
    borderStyle: "solid",
    marginBottom: 12,
  },
  tHead: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: C.sunk,
    borderBottomWidth: 0.6,
    borderBottomColor: C.rule,
    borderBottomStyle: "solid",
    paddingVertical: 4,
    paddingHorizontal: 7,
  },
  tNumber: { fontSize: 9, fontWeight: 700, color: C.tealDark, flex: 1 },
  tStatus: { fontSize: 9, fontWeight: 700 },
  tRow: {
    flexDirection: "row",
    borderBottomWidth: 0.6,
    borderBottomColor: C.rule,
    borderBottomStyle: "solid",
  },
  /* The last row carries no rule: the table's own border closes it. */
  tRowLast: { flexDirection: "row" },
  tLabel: {
    width: 92,
    fontSize: 8,
    fontWeight: 700,
    color: C.slate,
    paddingVertical: 5,
    paddingHorizontal: 7,
    borderRightWidth: 0.6,
    borderRightColor: C.rule,
    borderRightStyle: "solid",
  },
  tValue: { flex: 1, fontSize: 9, lineHeight: 1.5, paddingVertical: 5, paddingHorizontal: 7 },
  tPhotos: { flex: 1, flexDirection: "row", flexWrap: "wrap", paddingVertical: 5, paddingHorizontal: 7 },

  photo: { width: 74, height: 56, marginRight: 5, marginBottom: 5, objectFit: "cover" },
});
